import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { voicesAPI } from '../services/api.js';

// Vendor-aware voice <select> + a "listen" preview button, shared by the Personas
// editor and the campaign Voice field. The catalog (ElevenLabs + Deepgram/Aura) is
// the single source of selectable voices, fetched once from GET /voices/catalog.
//
// The selected value is the catalog KEY `${provider}:${id}` so a voiceId is
// unambiguous across vendors. Callers pass { provider, id } and get both back on
// change — they persist BOTH so the agent routes Deepgram vs ElevenLabs correctly.
//
// Props:
//   provider       — current voiceProvider ('elevenlabs' | 'deepgram'), or ''
//   voiceId        — current voiceId, or ''
//   onChange       — ({ provider, voiceId }) => void
//   includeDefault — when true, prepends a "— use tenant default —" ('' / '') option
//                    (campaign Voice field, which allows "no override")
//   className      — passed to the <select>
//   autoFocus

export function catalogKey(provider, id) {
  return provider && id ? `${provider}:${id}` : '';
}

function parseKey(key) {
  if (!key) return { provider: '', voiceId: '' };
  const i = key.indexOf(':');
  if (i === -1) return { provider: 'elevenlabs', voiceId: key };
  return { provider: key.slice(0, i), voiceId: key.slice(i + 1) };
}

// Module-level cache so every picker on the page shares one catalog fetch. We ONLY
// cache a non-empty result — an empty or failed fetch (e.g. a transient blip during
// a core redeploy) must NOT poison the cache, or every voice picker stays blank
// until a full page reload (the "no voices show" bug). Empty → reset so the next
// mount refetches.
let _catalogPromise = null;
function loadCatalog() {
  if (!_catalogPromise) {
    _catalogPromise = voicesAPI
      .catalog()
      .then((r) => {
        const list = Array.isArray(r?.data) ? r.data : [];
        if (list.length === 0) _catalogPromise = null; // don't cache empty — retry next time
        return list;
      })
      .catch(() => {
        _catalogPromise = null; // allow retry on next mount
        return [];
      });
  }
  return _catalogPromise;
}

export default function VoicePicker({
  provider = '',
  voiceId = '',
  onChange,
  includeDefault = false,
  className = 'field-input',
  autoFocus = false,
  // When set (e.g. ['cartesia','elevenlabs']), only voices from these providers
  // are offered — used to hide Deepgram/Kokoro in the exec-admin persona picker.
  allowProviders = null
}) {
  const [catalog, setCatalog] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let tries = 0;
    const attempt = () => {
      loadCatalog().then((data) => {
        if (!alive) return;
        setCatalog(data);
        // If the catalog came back empty (transient failure), retry a couple times
        // so a mounted picker recovers without needing a page reload.
        if ((!data || data.length === 0) && tries < 3) {
          tries += 1;
          setTimeout(attempt, 800 * tries);
        }
      });
    };
    attempt();
    return () => { alive = false; };
  }, []);

  // Stop + free the previous object URL whenever the selected voice changes or on
  // unmount, so we don't leak blobs or keep playing the wrong sample.
  useEffect(() => stopPreview, [provider, voiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlaying(false);
  };

  // When the caller has a voiceId but no explicit provider (e.g. the campaign
  // field, which historically stored only an ElevenLabs id), resolve the provider
  // from the catalog so the value matches an <option> and preview works.
  const effProvider = provider || (voiceId
    ? (catalog.find((v) => v.id === voiceId)?.provider || 'elevenlabs')
    : '');
  const value = catalogKey(effProvider, voiceId);
  // Current selection may be a voice not in the curated catalog (legacy row).
  const known = catalog.some((v) => v.provider === effProvider && v.id === voiceId);

  const handleSelect = (e) => {
    stopPreview();
    setPreviewError('');
    const sel = parseKey(e.target.value);
    onChange?.(sel);
  };

  const togglePreview = async () => {
    if (playing) { stopPreview(); return; }
    if (!effProvider || !voiceId) return;
    setPreviewError('');
    setLoadingPreview(true);
    try {
      const url = await voicesAPI.previewBlobUrl(effProvider, voiceId);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.onerror = () => { setPreviewError('Playback failed.'); setPlaying(false); };
      await audio.play();
      setPlaying(true);
    } catch (err) {
      setPreviewError(err.message || 'Preview unavailable.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const previewable = Boolean(effProvider && voiceId && known);

  return (
    <div>
      <div className="flex items-center gap-2">
        <select className={className} value={value} onChange={handleSelect} autoFocus={autoFocus}>
          {includeDefault && <option value="">— use tenant default —</option>}
          {!includeDefault && !value && <option value="">Select a voice…</option>}
          {/* Keep the current selection visible even if it's not in the catalog. */}
          {value && !known && <option value={value}>{voiceId} (current — {provider})</option>}
          {(allowProviders ? catalog.filter((v) => allowProviders.includes(v.provider)) : catalog).map((v) => (
            <option key={catalogKey(v.provider, v.id)} value={catalogKey(v.provider, v.id)}>
              {v.label} ({v.vendorLabel})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn-ghost shrink-0"
          onClick={togglePreview}
          disabled={!previewable || loadingPreview}
          title={previewable ? 'Listen to a sample' : 'Pick a catalog voice to preview'}
          aria-label="Listen to a sample"
        >
          {loadingPreview ? <Loader2 size={14} className="animate-spin" />
            : playing ? <Pause size={14} /> : <Play size={14} />}
        </button>
      </div>
      {previewError && <p className="mt-1 text-xs text-red-300">{previewError}</p>}
    </div>
  );
}
